/**
 * Central site configuration — single source of truth for business, legal, and
 * contact data. Do NOT hardcode these values in components, SEO helpers, or
 * structured-data builders; import from here instead. (The static index.html is
 * the one place that can't import this; keep it in sync manually.)
 */
export const SITE_CONFIG = {
  name: 'Byteet',
  url: 'https://byteet.md',
  defaultLocale: 'ro',
  locales: ['ro', 'ru'] as const,

  // Contact
  email: 'info@byteet.md',
  phone: '+37369746470', // E.164, for tel: links and schema
  phoneDisplay: '+373 69 746 470',

  // Postal address
  address: {
    streetAddress: '',
    postalCode: '',
    locality: 'Chișinău',
    country: 'MD',
  },

  // Legal identifiers (Moldova)
  legal: {
    entity: '„BYTEET SYSTEMS” S.R.L.',
    idno: '1026600013920',
  },

  // Social profiles
  social: {
    linkedin: 'https://www.linkedin.com/company/byteet',
    twitter: 'https://twitter.com/byteet',
  },
  twitterHandle: '@byteet',

  // Brand assets (site-relative)
  logo: '/logo-512.png',
  ogImage: '/og_image.jpg?v=2',

  availableLanguages: ['Romanian', 'Russian'],
  // Locale per site language, for dates and times (Intl).
  intlLocales: { ro: 'ro-RO', ru: 'ru-RU' },
  areaServed: 'Moldova',

  // Base paths of the main pages, before localization. Shared components build
  // links as getLocalizedRoute(SITE_CONFIG.routes.x, language), so they stay
  // identical across byteet and byteet_md, whose slugs differ.
  routes: {
    services: '/servicii',
    portfolio: '/portofoliu',
    articles: '/articole',
    about: '/despre',
    contact: '/contact',
    privacy: '/politica-de-confidentialitate',
    terms: '/termeni-si-conditii',
    book: '/programare',
  },

  // Online booking (/programare, /ru/zapis). Read by api/calendar-availability.ts,
  // api/calendar-book.ts and src/pages/Book.tsx, so changing a rule here
  // changes it everywhere. Times are wall-clock times in `timezone`, which is
  // also the clock the page shows them on: Chișinău here, Amsterdam on byteet.
  // Both sites check the same owner calendars, so they never double-book.
  booking: {
    timezone: 'Europe/Chisinau',
    /** ISO weekdays: 1 = Monday … 7 = Sunday. */
    workingDays: [1, 2, 3, 4, 5],
    /** First slot starts here; the last slot ends by `dayEnd`. HH:MM, 24h. */
    dayStart: '09:00',
    dayEnd: '17:00',
    slotMinutes: 30,
    /** Kept free before and after every busy block in the calendar. */
    bufferMinutes: 15,
    /** Earliest bookable slot is this far from now. */
    minNoticeHours: 4,
    /** Days ahead (counted from today in `timezone`) that can be booked. */
    horizonDays: 21,
    /** Event summary: "<prefix>: <name> (<company>)". */
    titlePrefix: 'Byteet intro call',
  },
} as const;

export type SiteConfig = typeof SITE_CONFIG;

/**
 * Canonical service categories — the single source of truth shared by the
 * contact forms (service selection) and the portfolio category filter. These
 * ids are stable and language-independent: they are what gets submitted to the
 * backend and stored in Jira. Display labels come from the `serviceOptions.*`
 * translation keys. Keep these in sync with the nine service pages
 * (ServicePage.tsx) and the homepage summaries.
 */
export const SERVICE_OPTIONS = [
  { id: 'web-software-development', labelKey: 'serviceOptions.webSoftwareDevelopment' },
  { id: 'mobile-app-development', labelKey: 'serviceOptions.mobileAppDevelopment' },
  { id: 'crm-development', labelKey: 'serviceOptions.crmDevelopment' },
  { id: 'erp-systems', labelKey: 'serviceOptions.erpSystems' },
  { id: 'ai-automation', labelKey: 'serviceOptions.aiAutomation' },
  { id: 'data-databases', labelKey: 'serviceOptions.dataDatabases' },
  { id: 'cloud-infrastructure', labelKey: 'serviceOptions.cloudInfrastructure' },
  { id: 'cybersecurity-identity', labelKey: 'serviceOptions.cybersecurityIdentity' },
  { id: 'managed-it-support', labelKey: 'serviceOptions.managedItSupport' },
] as const;

/** The "Other" option is always rendered last and reveals a free-text field. */
export const OTHER_SERVICE_OPTION = { id: 'other', labelKey: 'serviceOptions.other' } as const;

export type ServiceOptionId =
  | (typeof SERVICE_OPTIONS)[number]['id']
  | typeof OTHER_SERVICE_OPTION.id;
